import {
  ID,
  SortingMode,
  Task,
  TaskListTreeItem,
  Timestamp,
  WeekDays,
} from '@griply/types';
import { buildDeadlineTree, DeadlineGoal } from '@griply/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type Options = {
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  showEmptyOverdue?: boolean;
  showEmptyNoDate?: boolean;
  showEmptyDate?: boolean;
  dateFilterEndDate?: Timestamp | null;
  hideNewTaskRow?: boolean;
  deadlineGoals?: DeadlineGoal[];
};

export const useDeadlineTree = ({
  tasks,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  showEmptyOverdue,
  showEmptyNoDate,
  showEmptyDate,
  dateFilterEndDate,
  hideNewTaskRow,
  deadlineGoals,
}: Options): Record<ID, TaskListTreeItem> => {
  const { t } = useTranslation();
  const overdueLabel = t('general.sections.overdue.title');
  const noDeadlineLabel = t('general.labels.no-date');

  return useMemo<Record<ID, TaskListTreeItem>>(
    () =>
      buildDeadlineTree({
        tasks,
        customOrder,
        sortingMode,
        today,
        weekStartsOn,
        showEmptyOverdue,
        showEmptyNoDate,
        showEmptyDate,
        dateFilterEndDate,
        hideNewTaskRow,
        overdueLabel,
        noDeadlineLabel,
        rootLabel: 'Root',
        newTaskLabel: 'New Task',
        deadlineGoals,
      }),
    [
      today,
      tasks,
      showEmptyDate,
      showEmptyOverdue,
      showEmptyNoDate,
      sortingMode,
      weekStartsOn,
      dateFilterEndDate,
      hideNewTaskRow,
      customOrder,
      overdueLabel,
      noDeadlineLabel,
      deadlineGoals,
    ],
  );
};
